import React, { useState } from "react";
import { useKT } from "../i18n/kt";

/**
 * A supervisor's usual round: the looms they normally go to. It only orders and filters the list
 * (the server sends round looms first); it never stops anyone entering another loom when covering.
 */
export type MyRound = { set?: boolean; loomIds?: string[]; loomGroups?: string[] } | null | undefined;
export type RoundScope = "MINE" | "ALL";

const KEY = "hkp.karkhana.roundScope";

export const readRoundScope = (): RoundScope => {
  try {
    return localStorage.getItem(KEY) === "ALL" ? "ALL" : "MINE";
  } catch {
    return "MINE";
  }
};

/** My round / All looms, remembered on this device. My round is the default. */
export const useRoundScope = (): [RoundScope, (s: RoundScope) => void] => {
  const [scope, set] = useState<RoundScope>(readRoundScope);
  const change = (s: RoundScope) => {
    set(s);
    try {
      localStorage.setItem(KEY, s);
    } catch {
      // Storage blocked: the choice lasts until the page reloads.
    }
  };
  return [scope, change];
};

/**
 * Ids of the looms in the caller's round, or null when there is nothing to filter by: no round, or a
 * round that matches no loom in this list (e.g. its looms were retired). Null means All looms, no switch.
 */
export const roundLoomIds = (rounds: any): Set<string> | null => {
  if (!rounds?.myRound?.set) return null;
  const ids = new Set<string>((rounds.looms || []).filter((l: any) => l.inMyRound).map((l: any) => l.id));
  return ids.size ? ids : null;
};

/** "3 pending outside your round · Show all": work elsewhere is never hidden without a word. */
export const OutsideRoundNote = ({ pending, onShowAll }: { pending: number; onShowAll: () => void }) => {
  const { t } = useKT();
  if (!pending) return null;
  return (
    <p className="kw-muted kr-outside" role="status">
      <span>{pending === 1 ? t("1 pending outside your round") : t("{{n}} pending outside your round", { n: pending })}</span>
      {" · "}
      <button type="button" className="btn btn-link btn-sm p-0" onClick={onShowAll}>
        {t("Show all")}
      </button>
    </p>
  );
};

/** The two-way switch; `kind` picks the look of the page it sits on (Daily work or Rounds). */
export const RoundScopeSwitch = ({
  scope,
  onChange,
  count,
  kind = "kd",
}: {
  scope: RoundScope;
  onChange: (s: RoundScope) => void;
  count: number;
  kind?: "kd" | "kr";
}) => {
  const { t } = useKT();
  const items: [RoundScope, string][] = [
    ["MINE", t("My round ({{n}})", { n: count })],
    ["ALL", t("All looms")],
  ];
  return (
    <div className={kind === "kd" ? "kd-seg kd-seg--round" : "kr-counts kr-round"} role="group" aria-label={t("Which looms")}>
      {items.map(([k, label]) => (
        <button
          key={k}
          type="button"
          className={kind === "kd" ? (scope === k ? "is-on" : "") : `kr-choice${scope === k ? " is-on" : ""}`}
          aria-pressed={scope === k}
          onClick={() => onChange(k)}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

/** Quiet tag beside who entered an entry: they entered it for a loom outside their usual round. */
export const CoveringTag = ({ entry }: { entry: any }) => {
  const { t } = useKT();
  if (!entry?.covering) return null;
  return (
    <span className="kw-chip kr-covering" title={t("Entered while covering a loom outside their usual round")}>
      {t("Covering")}
    </span>
  );
};
